import { useMemo, useRef, useState } from 'react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { importPatients, ImportResponse, ImportRowResult } from '../services/api';
import { parseImportWorkbook, ParsedRow } from '../utils/importParser';

export const ImportPatients = () => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [rows, setRows] = useState<ParsedRow[]>([]);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [parseError, setParseError] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ImportResponse | null>(null);
  const [dragActive, setDragActive] = useState(false);

  const validCount = useMemo(() => rows.filter((r) => r.valid).length, [rows]);
  const invalidCount = rows.length - validCount;
  const selectedValidCount = useMemo(
    () => rows.filter((r) => r.valid && selected.has(r.rowNumber)).length,
    [rows, selected]
  );

  const reset = () => {
    setRows([]);
    setSelected(new Set());
    setParseError(null);
    setError(null);
    setResult(null);
    setFileName(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFile = async (file: File) => {
    reset();
    setFileName(file.name);
    try {
      const { rows: parsed, fatalError } = await parseImportWorkbook(file);
      if (fatalError) {
        setParseError(fatalError);
        return;
      }
      if (parsed.length === 0) {
        setParseError('No se encontraron filas de datos en la plantilla.');
        return;
      }
      setRows(parsed);
      // Pre-select every valid row.
      setSelected(new Set(parsed.filter((r) => r.valid).map((r) => r.rowNumber)));
    } catch (e) {
      setParseError(e instanceof Error ? e.message : 'No se pudo leer el archivo.');
    }
  };

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  };

  const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const onDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  const toggleRow = (rowNumber: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(rowNumber)) next.delete(rowNumber);
      else next.add(rowNumber);
      return next;
    });
  };

  const toggleAll = () => {
    const validRowNumbers = rows.filter((r) => r.valid).map((r) => r.rowNumber);
    setSelected((prev) =>
      validRowNumbers.every((n) => prev.has(n)) ? new Set() : new Set(validRowNumbers)
    );
  };

  const handleImport = async () => {
    setError(null);
    const payload = rows
      .filter((r) => r.valid && selected.has(r.rowNumber))
      .map((r) => r.payload);
    if (payload.length === 0) {
      setError('Seleccione al menos una fila válida para importar.');
      return;
    }
    setImporting(true);
    try {
      const res = await importPatients(payload);
      setResult(res);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo importar.');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="flex min-h-screen w-full flex-col p-6 bg-[#f6f7f8]">
      <Navbar showLogout={true} />

      <main className="flex flex-1 flex-col items-center justify-start py-6">
        <div className="w-full max-w-6xl">
          <div className="mb-6 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-[#101822]">Importar Pacientes</h1>
              <p className="text-sm text-slate-500">
                Cargue la plantilla de Excel para registrar pacientes, visitas y ventas en lote.
              </p>
            </div>
            <a
              href="/plantilla_importacion_pacientes.xlsx"
              download
              className="text-sm font-bold text-primary hover:underline"
            >
              Descargar plantilla
            </a>
          </div>

          {result ? (
            <ResultPanel result={result} onReset={reset} />
          ) : (
            <>
              {/* Upload */}
              <div className="mb-6 rounded-xl bg-white p-6 shadow-[0_4px_12px_0_rgba(0,0,0,0.05)]">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx"
                  onChange={onFileChange}
                  className="hidden"
                />
                <div
                  onDragOver={onDragOver}
                  onDragEnter={onDragOver}
                  onDragLeave={onDragLeave}
                  onDrop={onDrop}
                  className={`flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed p-8 text-center transition-colors ${
                    dragActive ? 'border-primary bg-blue-50' : 'border-slate-300'
                  }`}
                >
                  <p className="text-slate-600">
                    {fileName ? (
                      <span className="font-bold text-[#101822]">{fileName}</span>
                    ) : (
                      'Arrastre un archivo .xlsx aquí o selecciónelo manualmente.'
                    )}
                  </p>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="h-12 rounded-xl bg-primary px-6 font-bold text-white transition-colors hover:bg-blue-700"
                  >
                    {fileName ? 'Elegir otro archivo' : 'Elegir archivo'}
                  </button>
                </div>
              </div>

              {parseError && (
                <div className="mb-6 rounded-xl border border-red-200 bg-red-50 p-4">
                  <p className="text-red-700">{parseError}</p>
                </div>
              )}

              {error && (
                <div className="mb-6 rounded-xl border border-red-200 bg-red-50 p-4">
                  <p className="text-red-700">{error}</p>
                </div>
              )}

              {rows.length > 0 && (
                <>
                  {/* Summary */}
                  <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
                    <SummaryChip label="Filas leídas" value={rows.length} color="slate" />
                    <SummaryChip label="Válidas" value={validCount} color="green" />
                    <SummaryChip label="Con errores" value={invalidCount} color="red" />
                    <SummaryChip label="Seleccionadas" value={selectedValidCount} color="blue" />
                  </div>

                  {/* Preview table */}
                  <div className="overflow-x-auto rounded-xl bg-white shadow-[0_4px_12px_0_rgba(0,0,0,0.05)]">
                    <table className="w-full text-left text-sm">
                      <thead className="border-b border-slate-100 text-slate-500">
                        <tr>
                          <th className="p-3">
                            <input
                              type="checkbox"
                              aria-label="Seleccionar todas"
                              checked={validCount > 0 && selectedValidCount === validCount}
                              onChange={toggleAll}
                            />
                          </th>
                          <th className="p-3">Fila</th>
                          <th className="p-3">Paciente</th>
                          <th className="p-3">Llaves</th>
                          <th className="p-3">Visita</th>
                          <th className="p-3">Fecha</th>
                          <th className="p-3">Venta</th>
                          <th className="p-3">Estado</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-50">
                        {rows.map((r) => (
                          <tr key={r.rowNumber} className={r.valid ? 'hover:bg-slate-50' : 'bg-red-50/60'}>
                            <td className="p-3">
                              <input
                                type="checkbox"
                                disabled={!r.valid}
                                checked={selected.has(r.rowNumber)}
                                onChange={() => toggleRow(r.rowNumber)}
                                aria-label={`Seleccionar fila ${r.rowNumber}`}
                              />
                            </td>
                            <td className="p-3 text-slate-500">{r.rowNumber}</td>
                            <td className="p-3 font-medium text-[#101822]">{r.displayName}</td>
                            <td className="p-3 text-slate-500">{r.matchKeys}</td>
                            <td className="p-3">{r.hasVisit ? 'Sí' : '—'}</td>
                            <td className="p-3 text-slate-500">{r.visitDate || 'Hoy'}</td>
                            <td className="p-3">{r.hasSale ? 'Sí' : '—'}</td>
                            <td className="p-3">
                              {r.valid ? (
                                <span className="rounded-full bg-green-100 px-2 py-1 text-xs font-bold text-green-700">
                                  Lista
                                </span>
                              ) : (
                                <span className="text-xs text-red-600">{r.errors.join(' ')}</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="mt-6 flex items-center justify-end gap-3">
                    <button
                      onClick={reset}
                      className="h-12 rounded-xl bg-slate-200 px-6 font-bold text-slate-700 transition-colors hover:bg-slate-300"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={handleImport}
                      disabled={importing || selectedValidCount === 0}
                      className="h-12 rounded-xl bg-primary px-6 font-bold text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {importing ? 'Importando…' : `Importar ${selectedValidCount} fila(s)`}
                    </button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

const SummaryChip = ({
  label,
  value,
  color,
}: {
  label: string;
  value: number;
  color: 'slate' | 'green' | 'red' | 'blue';
}) => {
  const colors: Record<string, string> = {
    slate: 'bg-slate-100 text-slate-700',
    green: 'bg-green-100 text-green-700',
    red: 'bg-red-100 text-red-700',
    blue: 'bg-blue-100 text-blue-700',
  };
  return (
    <div className={`rounded-xl p-4 ${colors[color]}`}>
      <p className="text-2xl font-bold">{value}</p>
      <p className="text-xs font-medium">{label}</p>
    </div>
  );
};

const statusLabel = (r: ImportRowResult): { text: string; cls: string } => {
  switch (r.status) {
    case 'created':
      return { text: 'Paciente creado', cls: 'bg-green-100 text-green-700' };
    case 'matched':
      return { text: 'Paciente existente', cls: 'bg-blue-100 text-blue-700' };
    default:
      return { text: 'Omitida', cls: 'bg-red-100 text-red-700' };
  }
};

const ResultPanel = ({ result, onReset }: { result: ImportResponse; onReset: () => void }) => {
  const s = result.summary;
  return (
    <div>
      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-5">
        <SummaryChip label="Pacientes creados" value={s.patients_created} color="green" />
        <SummaryChip label="Pacientes existentes" value={s.patients_matched} color="blue" />
        <SummaryChip label="Visitas creadas" value={s.visits_created} color="slate" />
        <SummaryChip label="Ventas creadas" value={s.sales_created} color="slate" />
        <SummaryChip label="Omitidas" value={s.skipped} color="red" />
      </div>

      <div className="overflow-x-auto rounded-xl bg-white shadow-[0_4px_12px_0_rgba(0,0,0,0.05)]">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-slate-100 text-slate-500">
            <tr>
              <th className="p-3">Fila</th>
              <th className="p-3">Resultado</th>
              <th className="p-3">Detalle</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {result.results.map((r) => {
              const label = statusLabel(r);
              return (
                <tr key={r.row_number} className={r.status === 'skipped' ? 'bg-red-50/60' : ''}>
                  <td className="p-3 text-slate-500">{r.row_number}</td>
                  <td className="p-3">
                    <span className={`rounded-full px-2 py-1 text-xs font-bold ${label.cls}`}>
                      {label.text}
                    </span>
                  </td>
                  <td className="p-3 text-slate-600">
                    {r.error
                      ? r.error
                      : [
                          r.visit_id ? 'visita creada' : null,
                          r.sale_id ? 'venta creada' : null,
                        ]
                          .filter(Boolean)
                          .join(', ') || '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-6 flex justify-end">
        <button
          onClick={onReset}
          className="h-12 rounded-xl bg-primary px-6 font-bold text-white transition-colors hover:bg-blue-700"
        >
          Importar otro archivo
        </button>
      </div>
    </div>
  );
};
